import qs from 'qs'
import request from 'api/requestUtils'
import type { actions } from '@luxuryescapes/contract-svc-tag'

type GetManyTagsResponse = actions['getManyTags']['responses']['200']['content']['application/json']
type TagsPagination = GetManyTagsResponse['pagination']

export interface GetTagsByDestinationIdParams {
  destinationId: string
  region: string
  limit?: number
  offset?: number
  travelStartDate?: string
  travelEndDate?: string
  travelDateOverlap?: boolean
  tagType?: App.OfferTagType
  includeAssignments?: boolean
  includedAssignmentEntityTypeIn?: Array<App.OfferTagAssignment['entityType']>
}

export interface GetTagsByOfferIdParams {
  offerId: string
  region: string
  limit?: number
  offset?: number
  travelStartDate?: string
  travelEndDate?: string
  travelDateOverlap?: boolean
  tagType?: App.OfferTagType
  includeAssignments?: boolean
  includedAssignmentEntityTypeIn?: Array<App.OfferTagAssignment['entityType']>
}

export interface GetTagsResult {
  tags: Array<App.OfferTag>
  pagination: TagsPagination
}

/**
 * Retrieves tags assigned to a destination by destination ID
 * @param params - Parameters including destinationId and optional pagination
 * @returns Promise with tags (including names) and pagination info
 */
export function getTagsByDestinationId(
  params: GetTagsByDestinationIdParams,
): Promise<GetTagsResult> {
  const {
    destinationId,
    region,
    limit,
    offset,
    travelStartDate,
    travelEndDate,
    travelDateOverlap,
    tagType,
    includeAssignments,
    includedAssignmentEntityTypeIn,
  } = params

  const query = qs.stringify({
    entityIdIn: [destinationId],
    entityTypeIn: ['DESTINATION'],
    region,
    ...(limit !== undefined && { limit }),
    ...(offset !== undefined && { offset }),
    ...(travelStartDate !== undefined && { travelStartDate }),
    ...(travelEndDate !== undefined && { travelEndDate }),
    ...(travelDateOverlap !== undefined && { travelDateOverlap }),
    ...(tagType !== undefined && { tagType }),
    ...(includeAssignments !== undefined && { includeAssignments }),
    ...(includedAssignmentEntityTypeIn !== undefined && { includedAssignmentEntityTypeIn }),
  }, {
    arrayFormat: 'brackets',
  })

  return request
    .get<GetManyTagsResponse>(`/api/tags/v1/tags?${query}`)
    .then((response) => ({
      tags: response.result,
      pagination: response.pagination,
    }))
}

/**
 * Retrieves tags assigned to an offer by offer ID
 * @param params - Parameters including offerId and optional pagination
 * @returns Promise with tags (including names) and pagination info
 */
export function getTagsByOfferId(
  params: GetTagsByOfferIdParams,
): Promise<GetTagsResult> {
  const {
    offerId,
    region,
    limit,
    offset,
    travelStartDate,
    travelEndDate,
    travelDateOverlap,
    tagType,
    includeAssignments,
    includedAssignmentEntityTypeIn,
  } = params

  const query = qs.stringify({
    entityIdIn: [offerId],
    region,
    ...(limit !== undefined && { limit }),
    ...(offset !== undefined && { offset }),
    ...(travelStartDate !== undefined && { travelStartDate }),
    ...(travelEndDate !== undefined && { travelEndDate }),
    ...(travelDateOverlap !== undefined && { travelDateOverlap }),
    ...(tagType !== undefined && { tagType }),
    ...(includeAssignments !== undefined && { includeAssignments }),
    ...(includedAssignmentEntityTypeIn !== undefined && { includedAssignmentEntityTypeIn }),
  }, {
    arrayFormat: 'brackets',
  })

  return request
    .get<GetManyTagsResponse>(`/api/tags/v1/tags?${query}`)
    .then((response) => ({
      tags: response.result,
      pagination: response.pagination,
    }))
}
